/**
 * ConversationStageSelector — seletor de etapa do lead no header da conversa.
 *
 * Modelo: o correto é UM lead ativo por contato, mas 2+ são possíveis (legado) e
 * o componente lida com ambos. "Ativo" = pendente (nunca won/lost/excluído).
 *
 * UX unificada em DRILL-DOWN (igual pra adicionar e editar):
 *   Nível 1 → lista de funis (o funil onde há lead vem marcado com a etapa atual).
 *   Nível 2 → etapas do funil clicado, com ✓ na etapa atual do lead + "voltar aos funis".
 *   Escolher uma etapa:
 *     - funil já tem lead      → move esse lead pra etapa escolhida
 *     - 1 lead em outro funil  → move o lead pra este funil/etapa (troca de funil)
 *     - nenhum lead            → cria o lead neste funil/etapa
 *     - 2+ leads, funil vazio  → move o lead principal (caso de borda)
 *
 * Pílula fechada: sem lead → "Adicionar ao funil"; com lead → etapa atual (+ "N funis"
 * quando há mais de um lead ativo).
 *
 * Gates no módulo `crm`: crm.read → vê · crm.update → move · crm.create → adiciona.
 * Wrapper→inner: hooks de dados vivem no inner, que só monta com permissão + contato.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type { ConversationResponse, LeadResponse } from '@troia-v3/types';
import { usePermissions } from '@/hooks/usePermissions';
import { useLeadsByContact, useUpdateLead, useCreateLead } from '@/modules/crm/leads/hooks';
import { useStepsByFunnelId, useFunnelsDropdown } from '@/modules/funnels/hooks';

const PURPLE = '#8b5cf6';

/** Largura do menu em portal — usada no clamp de posição E no próprio Menu. */
const MENU_W = 264;

/** Alvo de toque mínimo da Apple. Vale pro gatilho E pros itens da folha de baixo. */
const TOUCH_TARGET = 44;

/** Lead "ativo" = pendente. Nunca ganho/perdido (won/lost). Excluídos já não vêm do endpoint. */
const isActiveLead = (l: LeadResponse): boolean => l.businessStatus !== 'won' && l.businessStatus !== 'lost';

// Ícones inline (evita depender de nomes do catálogo de Icon)
const ChevronDown: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M6 9l6 6 6-6" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight: React.FC<{ color?: string }> = ({ color = '#94a3b8' }) => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M9 6l6 6-6 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Check: React.FC = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M5 13l4 4L19 7" stroke={PURPLE} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Plus: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const Back: React.FC = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M15 18l-6-6 6-6" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dot: React.FC<{ color: string; size?: number }> = ({ color, size = 9 }) => (
  <span style={{ width: size, height: size, borderRadius: '50%', backgroundColor: color, flex: '0 0 auto' }} />
);

// ============================================================================
// Wrapper — gates de permissão (não chama hooks de dados)
// ============================================================================
interface ConversationStageSelectorProps {
  conversation: ConversationResponse;
  /** 'mobile' = pílula de 36px em botão de 44px e menu em bottom sheet (faixa M1). */
  variant?: 'desktop' | 'mobile';
}

const ConversationStageSelector: React.FC<ConversationStageSelectorProps> = ({ conversation, variant = 'desktop' }) => {
  const { hasModulePermission } = usePermissions();
  const canRead = hasModulePermission('crm', 'read');
  const canUpdate = hasModulePermission('crm', 'update');
  const canCreate = hasModulePermission('crm', 'create');

  // contactId da conversa; fallback pro id do contato embutido (nem sempre o FK vem populado)
  const contactId = conversation.contactId || conversation.contact?.id;
  const isIndividual = conversation.conversationType !== 'group';

  if (!canRead || !isIndividual || !contactId) return null;

  return (
    <StageSelectorInner
      contactId={contactId}
      leadId={conversation.leadId}
      canUpdate={canUpdate}
      canCreate={canCreate}
      variant={variant}
    />
  );
};

// ============================================================================
// Inner — dados + estados + dropdown
// ============================================================================
interface InnerProps {
  contactId: string;
  leadId?: string;
  canUpdate: boolean;
  canCreate: boolean;
  variant: 'desktop' | 'mobile';
}

const StageSelectorInner: React.FC<InnerProps> = ({ contactId, leadId, canUpdate, canCreate, variant }) => {
  const isMobile = variant === 'mobile';
  const { data: leads = [], isLoading } = useLeadsByContact(contactId);
  const { data: funnels = [] } = useFunnelsDropdown();

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Drill: funil aberto no nível 2 (null = nível 1, lista de funis). Vale pra add e edit.
  const [drillFunnelId, setDrillFunnelId] = useState<string | null>(null);

  const updateMutation = useUpdateLead();
  const createMutation = useCreateLead();
  const pending = updateMutation.isPending || createMutation.isPending;

  // Só leads ativos (pendentes).
  const activeLeads = useMemo(() => leads.filter(isActiveLead), [leads]);
  const multi = activeLeads.length > 1;

  // Lista de funis do nível 1: funis ativos do dropdown + funil(is) onde há lead ativo que NÃO
  // vêm no dropdown (funil inativo/arquivado — o lead persiste nele). Sem o merge, o funil atual
  // do lead ficaria invisível e não-drillável no menu.
  const funnelList = useMemo(() => {
    const base = funnels.map((f) => ({ id: f.id, name: f.name, color: f.color || PURPLE }));
    const inList = new Set(base.map((f) => f.id));
    const seen = new Set<string>();
    const orphans: Array<{ id: string; name: string; color: string }> = [];
    for (const l of activeLeads) {
      if (l.funnelId && !inList.has(l.funnelId) && !seen.has(l.funnelId)) {
        seen.add(l.funnelId);
        orphans.push({ id: l.funnelId, name: l.funnel?.name || 'Funil', color: PURPLE });
      }
    }
    return [...base, ...orphans];
  }, [funnels, activeLeads]);

  const funnelMeta = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    funnelList.forEach((f) => map.set(f.id, { name: f.name, color: f.color }));
    return map;
  }, [funnelList]);

  // Mapa funil → lead ativo naquele funil (marca no nível 1 e ✓ no nível 2)
  const leadByFunnel = useMemo(() => {
    const map = new Map<string, LeadResponse>();
    activeLeads.forEach((l) => { if (l.funnelId) map.set(l.funnelId, l); });
    return map;
  }, [activeLeads]);

  // Lead ativo principal: o vinculado à conversa; senão o de updatedAt mais recente
  const primaryLead: LeadResponse | undefined = useMemo(() => {
    if (!activeLeads.length) return undefined;
    if (leadId) {
      const linked = activeLeads.find((l) => l.id === leadId);
      if (linked) return linked;
    }
    return [...activeLeads].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))[0];
  }, [activeLeads, leadId]);

  // Cor da etapa atual (pílula) — a cor vem dos steps do funil; o nome vem do próprio lead
  const { data: primarySteps = [] } = useStepsByFunnelId(primaryLead?.funnelId);
  const primaryColor = primarySteps.find((s) => s.id === primaryLead?.stepId)?.color || PURPLE;
  const primaryName = primaryLead?.step?.name || 'Sem etapa';

  // Contador "+N funis" (só quando há mais de um lead ativo)
  const distinctFunnels = useMemo(() => new Set(activeLeads.map((l) => l.funnelId).filter(Boolean)).size, [activeLeads]);
  const counterLabel = distinctFunnels > 1 ? `${distinctFunnels} funis` : `${activeLeads.length} leads`;

  // Funis ordenados: os que têm lead primeiro (para aparecerem no topo da lista)
  const sortedFunnels = useMemo(() => {
    const withLead = funnelList.filter((f) => leadByFunnel.has(f.id));
    const rest = funnelList.filter((f) => !leadByFunnel.has(f.id));
    return [...withLead, ...rest];
  }, [funnelList, leadByFunnel]);

  const hasLead = !!primaryLead;
  // A pílula só abre o dropdown se puder agir: editar (canUpdate) ou adicionar (canCreate)
  const canOpen = hasLead ? canUpdate : canCreate;

  // O Menu é `position: fixed` num portal, então usa as coordenadas CRUAS do
  // getBoundingClientRect (que já são de viewport) — somar window.scrollX/Y
  // empurrava o menu pra fora da tela em página rolada.
  const computePos = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    let left = Math.min(r.left, window.innerWidth - MENU_W - 8);
    left = Math.max(8, left);
    setPos({ top: r.bottom + 6, left });
  };

  const openMenu = () => {
    setDrillFunnelId(null);
    setOpen(true);
  };
  const closeMenu = () => { setOpen(false); setDrillFunnelId(null); };

  // Reposiciona em resize e scroll (capture pega o scroll de QUALQUER container
  // rolável, não só o da janela) — mesma mecânica do ContextSelector. Sem isso o
  // menu calculava a posição uma vez e desalinhava ao rolar o header.
  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const moveLead = (lead: LeadResponse, funnelId: string, stepId: string) => {
    if (lead.funnelId === funnelId && lead.stepId === stepId) { closeMenu(); return; }
    // Toast fiel à origem: sem funil anterior = adição; mesmo funil = troca de etapa; outro = movido
    const label = !lead.funnelId
      ? 'Lead adicionado ao funil'
      : lead.funnelId === funnelId ? 'Etapa atualizada' : 'Lead movido de funil';
    updateMutation.mutate(
      { id: lead.id, data: { funnelId, stepId } },
      {
        onSuccess: () => toast.success(label),
        onError: () => toast.error('Erro ao mover o lead'),
      }
    );
    closeMenu();
  };

  const createLead = async (funnelId: string, stepId: string) => {
    try {
      await createMutation.mutateAsync({ contactId, funnelId, stepId, segment: '' });
      toast.success('Lead adicionado ao funil');
      closeMenu();
    } catch {
      toast.error('Erro ao adicionar ao funil');
    }
  };

  // Ação ao escolher uma etapa (nível 2), decidindo entre mover/criar
  const handlePickStage = (funnelId: string, stepId: string) => {
    const leadHere = leadByFunnel.get(funnelId);
    if (leadHere) return moveLead(leadHere, funnelId, stepId);
    if (activeLeads.length === 1) return moveLead(activeLeads[0], funnelId, stepId);
    if (!primaryLead) return void createLead(funnelId, stepId);
    return moveLead(primaryLead, funnelId, stepId); // 2+ leads, funil sem lead → move o principal
  };

  // ---------- estado: carregando ----------
  if (isLoading) {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full"
        style={{
          padding: '3px 9px',
          backgroundColor: 'rgba(148,163,184,0.14)',
          // A faixa M1 alinha os filhos pelo TOPO (é lá que o alvo de toque de 44px
          // começa); o esqueleto não tem 44px, então se centraliza sozinho.
          ...(isMobile ? { alignSelf: 'center', flex: '0 0 auto' } : {}),
        }}
        data-testid="stage-selector-loading"
      >
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(148,163,184,0.5)' }} />
        <span style={{ width: 62, height: 8, borderRadius: 5, background: 'rgba(148,163,184,0.35)' }} />
      </span>
    );
  }

  // Sem lead e sem permissão de criar → não renderiza nada
  if (!hasLead && !canCreate) return null;

  const trigger = isMobile ? (
    // Celular: o respiro vertical é padding DO BOTÃO (44px de alvo de toque),
    // e a pílula visual de 36px vive no <span> interno. Margem não é clicável.
    <button
      ref={triggerRef}
      type="button"
      onClick={(e) => { stop(e); if (hasLead && !canOpen) return; open ? closeMenu() : openMenu(); }}
      style={{ height: TOUCH_TARGET, padding: '4px 0', background: 'transparent', border: 'none', flex: '0 0 auto', cursor: canOpen ? 'pointer' : 'default' }}
      data-testid={hasLead ? 'stage-selector-trigger' : 'stage-add-to-funnel'}
      title={hasLead ? primaryName : 'Adicionar ao funil'}
    >
      <span style={mobilePillStyle(!hasLead)}>
        {hasLead ? (
          <>
            <Dot color={primaryColor} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{primaryName}</span>
            {multi && (
              <span style={{ flex: '0 0 auto', backgroundColor: 'rgba(139,92,246,0.22)', fontSize: 11, fontWeight: 700, padding: '0 6px', borderRadius: 999 }}>
                {counterLabel}
              </span>
            )}
            {canOpen && <ChevronDown color="currentColor" />}
          </>
        ) : (
          <>
            <Plus color={PURPLE} />
            Adicionar ao funil
          </>
        )}
      </span>
    </button>
  ) : hasLead ? (
    <button
      ref={triggerRef}
      type="button"
      onClick={(e) => { stop(e); if (!canOpen) return; open ? closeMenu() : openMenu(); }}
      className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors"
      style={{
        padding: '2px 8px',
        color: 'var(--theme-card-text)',
        backgroundColor: 'rgba(148,163,184,0.14)',
        border: '1px solid var(--theme-card-border)',
        cursor: canOpen ? 'pointer' : 'default',
        // A coluna CRM do header ganhou piso (90px) e passou a poder encolher: sem
        // `minWidth: 0` + `overflow` o mínimo automático deste botão é o texto inteiro,
        // ele se recusa a encolher e o déficit inteiro cai na coluna do NOME, que sumia.
        minWidth: 0,
        overflow: 'hidden',
      }}
      data-testid="stage-selector-trigger"
      title={canOpen ? 'Mudar etapa / funil do lead' : 'Etapa do lead'}
    >
      <Dot color={primaryColor} />
      {/* `chat-hdr-pill-text` = gancho de reticências do header (src/index.css). O nome
          inteiro fica no `title` deste span, que vence o `title` do botão no hover. */}
      <span className="chat-hdr-pill-text" title={primaryName}>{primaryName}</span>
      {multi && (
        <span style={{ flexShrink: 0, backgroundColor: 'rgba(139,92,246,0.14)', color: 'var(--theme-sidebar-active)', fontSize: 10, fontWeight: 700, padding: '0 6px', borderRadius: 999, marginLeft: 1 }}>
          {counterLabel}
        </span>
      )}
      {canOpen && <ChevronDown color="currentColor" />}
    </button>
  ) : (
    <button
      ref={triggerRef}
      type="button"
      onClick={(e) => { stop(e); open ? closeMenu() : openMenu(); }}
      className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors"
      style={{
        padding: '3px 10px',
        color: PURPLE,
        backgroundColor: 'rgba(139,92,246,0.10)',
        border: '1px dashed rgba(139,92,246,0.45)',
        // Mesmo motivo do gatilho preenchido: a coluna CRM encolhe. Sem `nowrap` +
        // `overflow` este rótulo QUEBRAVA EM DUAS LINHAS (botão de 40px dentro da linha
        // de 22px do header) e era cortado pelo `overflow:hidden` da coluna.
        whiteSpace: 'nowrap',
        minWidth: 0,
        overflow: 'hidden',
      }}
      data-testid="stage-add-to-funnel"
      title="Adicionar ao funil"
    >
      <Plus color={PURPLE} />
      <span className="chat-hdr-pill-text">Adicionar ao funil</span>
    </button>
  );

  return (
    <>
      {trigger}
      {open && createPortal(
        <>
          {/* Scrim: invisível no computador (só captura o clique-fora), escuro no
              celular — mesmo valor do scrim do SearchableSelect em `variant='sheet'`,
              que é a folha do campo Cliente na MESMA faixa. */}
          <div
            className="fixed inset-0 z-[100]"
            style={isMobile ? { backgroundColor: 'rgba(15,23,42,0.45)' } : undefined}
            onClick={(e) => { e.stopPropagation(); closeMenu(); }}
          />
          <Menu pos={pos} sheet={isMobile} onClick={stop} testId="stage-selector-menu">
            {drillFunnelId === null ? (
              // -------- Nível 1: lista de funis --------
              <>
                <div style={dropdownHeadStyle}>{hasLead ? 'Mover para' : 'Escolha o funil'}</div>
                {sortedFunnels.length === 0 && <div style={emptyStyle}>Nenhum funil disponível</div>}
                {sortedFunnels.map((f) => {
                  const leadHere = leadByFunnel.get(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={(e) => { stop(e); setDrillFunnelId(f.id); }}
                      style={{ ...itemStyle(!!leadHere, isMobile), cursor: 'pointer' }}
                      data-testid={`funnel-option-${f.id}`}
                      onMouseEnter={(ev) => { if (!leadHere) ev.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.backgroundColor = leadHere ? '#f6f4ff' : 'transparent'; }}
                    >
                      <Dot color={f.color || PURPLE} />
                      <span style={itemNameStyle}>{f.name}</span>
                      {leadHere && (
                        <span style={stageBadgeStyle} title="Etapa atual do lead">{leadHere.step?.name || 'sem etapa'}</span>
                      )}
                      <ChevronRight />
                    </button>
                  );
                })}
              </>
            ) : (
              // -------- Nível 2: etapas do funil escolhido --------
              <>
                <button
                  type="button"
                  onClick={(e) => { stop(e); setDrillFunnelId(null); }}
                  style={backBtnStyle(isMobile)}
                  data-testid="stage-back-to-funnels"
                >
                  <Back /> voltar aos funis
                </button>
                <StageSection
                  sheet={isMobile}
                  funnelId={drillFunnelId}
                  funnelName={funnelMeta.get(drillFunnelId)?.name}
                  funnelColor={funnelMeta.get(drillFunnelId)?.color}
                  currentStepId={leadByFunnel.get(drillFunnelId)?.stepId}
                  interactive={!pending}
                  onPick={(stepId) => handlePickStage(drillFunnelId, stepId)}
                />
              </>
            )}
          </Menu>
        </>,
        document.body
      )}
    </>
  );
};

// ============================================================================
// StageSection — busca etapas de um funil e lista (lazy: monta só ao drillar o funil)
// ============================================================================
interface SectionProps {
  /** true = folha de baixo do celular: itens com alvo de toque de 44px. */
  sheet: boolean;
  funnelId?: string;
  funnelName?: string;
  funnelColor?: string;
  currentStepId?: string;
  interactive: boolean;
  onPick: (stepId: string) => void;
}

const StageSection: React.FC<SectionProps> = ({ sheet, funnelId, funnelName, funnelColor, currentStepId, interactive, onPick }) => {
  const { data: steps = [], isLoading } = useStepsByFunnelId(funnelId);
  const ordered = useMemo(() => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [steps]);

  return (
    <div>
      <div style={{ ...dropdownHeadStyle, display: 'flex', alignItems: 'center', gap: 7 }}>
        {funnelColor && <Dot color={funnelColor} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{funnelName || 'Funil'}</span>
      </div>
      {isLoading && <div style={emptyStyle}>Carregando etapas…</div>}
      {!isLoading && ordered.length === 0 && <div style={emptyStyle}>Sem etapas</div>}
      {ordered.map((s) => {
        const isCurrent = s.id === currentStepId;
        return (
          <button
            key={s.id}
            type="button"
            disabled={!interactive || isCurrent}
            onClick={(e) => { e.stopPropagation(); onPick(s.id); }}
            style={{ ...itemStyle(isCurrent, sheet), cursor: interactive && !isCurrent ? 'pointer' : 'default' }}
            data-testid={`stage-option-${s.id}`}
            onMouseEnter={(ev) => { if (interactive && !isCurrent) ev.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(ev) => { ev.currentTarget.style.backgroundColor = isCurrent ? '#f6f4ff' : 'transparent'; }}
          >
            <Dot color={s.color || PURPLE} />
            <span style={itemNameStyle}>{s.name}</span>
            {isCurrent && <Check />}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// Menu (portal, tema claro fixo — molde CardActionsMenu)
// ============================================================================
const Menu: React.FC<{ pos: { top: number; left: number }; sheet?: boolean; onClick: (e: React.MouseEvent) => void; testId: string; children: React.ReactNode }> = ({ pos, sheet = false, onClick, testId, children }) => (
  <div
    className="fixed z-[101]"
    style={sheet ? {
      // Celular: folha de baixo, colada nas três bordas. Réplica da folha do
      // `SearchableSelect` em `variant='sheet'` (raio 16 = `rounded-t-2xl`, alça
      // 36×4 em #e2e8f0, safe-area no rodapé) — é a folha do campo Cliente na
      // MESMA faixa, e duas folhas vizinhas com raios diferentes saltam à vista.
      // Tema claro fixo igual ao popover: é superfície de overlay.
      left: 0,
      right: 0,
      bottom: 0,
      maxHeight: 'min(70vh, 480px)',
      overflowY: 'auto',
      backgroundColor: '#ffffff',
      borderTop: '1px solid #e5e7eb',
      borderRadius: '16px 16px 0 0',
      boxShadow: '0 -10px 30px rgba(15,23,42,.22)',
      padding: 8,
      paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    } : {
      top: pos.top,
      left: pos.left,
      width: MENU_W,
      maxHeight: 'min(70vh, 440px)',
      overflowY: 'auto',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      boxShadow: '0 10px 30px rgba(16,12,40,.16), 0 2px 8px rgba(16,12,40,.08)',
      padding: 6,
    }}
    onClick={onClick}
    data-testid={testId}
    data-variant={sheet ? 'sheet' : 'popover'}
  >
    {sheet && (
      <div className="flex justify-center pt-1 pb-2" aria-hidden="true">
        <span className="w-9 h-1 rounded-full" style={{ backgroundColor: '#e2e8f0' }} />
      </div>
    )}
    {children}
  </div>
);

// estilos compartilhados (dropdown = tema claro fixo)
const dropdownHeadStyle: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94a3b8', padding: '7px 10px 5px', fontWeight: 600,
};
const itemNameStyle: React.CSSProperties = { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' };
const emptyStyle: React.CSSProperties = { fontSize: 12.5, color: '#94a3b8', padding: '6px 10px' };
const stageBadgeStyle: React.CSSProperties = {
  flex: '0 0 auto', fontSize: 11, fontWeight: 500, color: '#475569', background: '#eef1f5',
  padding: '1px 7px', borderRadius: 999, maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
/** "voltar aos funis" — também é dedo na folha de baixo, então também cresce pros 44px. */
function backBtnStyle(sheet: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: sheet ? 10 : 6, width: '100%',
    padding: sheet ? '12px 14px' : '6px 10px',
    minHeight: sheet ? TOUCH_TARGET : undefined,
    fontSize: sheet ? 15 : 12,
    color: '#64748b', background: 'transparent', border: 'none', cursor: 'pointer',
  };
}
/**
 * Pílula do celular (faixa M1): 36px de altura dentro do botão de 44px, texto de
 * 13px da escala iOS — nunca classe Tailwind de tamanho de texto.
 *
 * Cores do mockup M1 (`.mp.violet` / `.mp.dash`): a pílula do CRM é a ÚNICA
 * violeta da faixa. No celular não existe a legenda "CRM" em cima da pílula (no
 * computador é ela que identifica a coluna), então quem faz esse trabalho é a cor.
 * A tracejada repete verbatim a do "Vincular cliente" (Task 14), pílula vizinha
 * na mesma faixa.
 */
function mobilePillStyle(dashed: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 36,
    padding: '0 12px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: 210,
    ...(dashed
      ? { color: PURPLE, backgroundColor: 'rgba(139,92,246,0.10)', border: '1px dashed rgba(139,92,246,0.45)' }
      : { color: 'var(--theme-sidebar-active)', backgroundColor: 'rgba(139,92,246,0.14)', border: '1px solid transparent' }),
  };
}

/**
 * Item do menu. `sheet` = folha de baixo do celular: o item é DEDO, então cresce
 * pro alvo de toque de 44px (mockup `.msi`: 13px de respiro, texto 15, raio 12).
 */
function itemStyle(active: boolean, sheet = false): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: sheet ? 13 : 10, width: '100%',
    padding: sheet ? '13px 14px' : '8px 10px',
    minHeight: sheet ? TOUCH_TARGET : undefined,
    borderRadius: sheet ? 12 : 8,
    fontSize: sheet ? 15 : 13.5,
    color: '#1e293b', background: active ? '#f6f4ff' : 'transparent',
    border: 'none', fontWeight: active ? 600 : 400,
  };
}

export default ConversationStageSelector;
export { ConversationStageSelector };
